import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../services/api";
import {
  PhoneOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  UserOutlined,
  HomeOutlined,
  BankOutlined,
  AppstoreOutlined,
  ColumnWidthOutlined,
  ApartmentOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { FaWhatsapp } from "react-icons/fa";
import { Spin, Empty, message } from "antd";
import "./CommercialSpaceDetails.css";

const TABS = ["Details", "Description", "Location", "Amenities"];

function CommercialSpaceDetails() {
  const { slug } = useParams();
  const [space, setSpace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("Details");
  const [imageErrors, setImageErrors] = useState({});
  const hasTrackedView = useRef(false);
  const stripRef = useRef(null);
  const [images, setImages] = useState([]);
  const [otherImages, setOtherImages] = useState([]);

  const handleNext = useCallback(() => {
    if (otherImages.length > 0) {
      setPreviewIndex((prev) => (prev + 1) % otherImages.length);
    }
  }, [otherImages.length]);

  const handlePrev = useCallback(() => {
    if (otherImages.length > 0) {
      setPreviewIndex((prev) => (prev === 0 ? otherImages.length - 1 : prev - 1));
    }
  }, [otherImages.length]);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
  }, []);

  const handleImageError = useCallback((index, isMain = false) => {
    setImageErrors((prev) => ({ ...prev, [index]: true }));
    if (isMain && images.length > 1) {
      const nextAvailableIndex = images.findIndex((_, idx) => !imageErrors[idx] && idx !== index);
      if (nextAvailableIndex !== -1) {
        setActiveImage(nextAvailableIndex);
      }
    }
  }, [images, imageErrors]);

  const getVisitorId = () => {
    let visitorId = localStorage.getItem("visitorId");
    if (!visitorId) {
      visitorId = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("visitorId", visitorId);
    }
    return visitorId;
  };

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = user?.role === "admin";

  const formatDate = (date) => {
    if (!date) return "N/A";
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? "N/A" : parsed.toLocaleDateString();
  };

  const normalizePhone = (value) => value?.replace(/\D/g, "") || "";

  const getPriceLabel = (type) => {
    if (type === "rent") return "/month";
    if (type === "lease") return "/year";
    return "";
  };

  const renderDetailRow = (label, value) =>
    value !== null && value !== undefined && value !== "" ? (
      <tr key={label}>
        <td className="csp-detail-label">{label}</td>
        <td className="csp-detail-value">{value}</td>
      </tr>
    ) : null;

  const renderImageWithFallback = (src, alt, className, index, isMain = false) => {
    if (imageErrors[index]) {
      return (
        <div className={`${className} csp-image-fallback`}>
          <span>Image not available</span>
        </div>
      );
    }
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        onError={() => handleImageError(index, isMain)}
        loading="lazy"
      />
    );
  };

  useEffect(() => {
    const fetchCommercialSpace = async () => {
      try {
        setLoading(true);
        let res;
        try {
          res = await API.get(`/commercial-spaces/slug/${slug}`);
        } catch (err) {
          try {
            res = await API.get(`/commercial-spaces/${slug}`);
          } catch (error) {
            console.log("Both slug and ID fetch failed:", error);
            setSpace(null);
            message.error("Failed to load commercial space details");
            return;
          }
        }
        setSpace(res.data);

        if (res.data?._id && !hasTrackedView.current) {
          hasTrackedView.current = true;
          try {
            const visitorId = getVisitorId();
            const viewRes = await API.post(
              `/commercial-spaces/${res.data._id}/view`,
              {},
              { headers: { "x-visitor-id": visitorId } }
            );
            setSpace((prev) =>
              prev
                ? {
                  ...prev,
                  views:
                    typeof viewRes.data.views === "number"
                      ? viewRes.data.views
                      : prev.views,
                }
                : prev
            );
          } catch (viewError) {
            console.log("View count error:", viewError);
          }
        }
      } catch (err) {
        console.log(err);
        setSpace(null);
        message.error("Error loading commercial space details");
      } finally {
        setLoading(false);
      }
    };

    hasTrackedView.current = false;
    setActiveImage(0);
    fetchCommercialSpace();
  }, [slug]);

  useEffect(() => {
    if (space) {
      const thumbnailOnly = space.thumbnail ? [space.thumbnail] : [];
      const otherImagesList = (space.images || []).filter((img) => img !== space.thumbnail);

      if (otherImagesList.length > 0) {
        setImages([otherImagesList[0]]);
        setOtherImages(otherImagesList);
      } else {
        setImages(thumbnailOnly);
        setOtherImages([]);
      }
      setImageErrors({});
      setActiveImage(0);
    }
  }, [space]);

  useEffect(() => {
    if (previewOpen && stripRef.current) {
      const activeThumb = stripRef.current.children[previewIndex];
      if (activeThumb) {
        activeThumb.scrollIntoView({ block: "nearest", inline: "center" });
      }
    }
  }, [previewIndex, previewOpen]);

  useEffect(() => {
    if (previewOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [previewOpen]);

  if (loading) {
    return (
      <div className="csp-state">
        <Spin size="large" />
      </div>
    );
  }

  if (!space) {
    return (
      <div className="csp-state">
        <Empty description="Commercial space not found" />
      </div>
    );
  }

  const phone = normalizePhone(space.contactDetails?.phone);
  const fullLocation = [
    space.location?.address,
    space.location?.city,
    space.location?.state,
    space.location?.country,
  ]
    .filter(Boolean)
    .join(", ");

  const cacheKey = space.updatedAt ? `?v=${encodeURIComponent(space.updatedAt)}` : "";

  return (
    <div className="csp-root">
      {/* Gallery */}
      <section className="csp-gallery">
        <div
          className="csp-gallery__main"
          onClick={() => {
            setPreviewIndex(0);
            setPreviewOpen(true);
          }}
        >
          {otherImages.length > 0 ? (
            renderImageWithFallback(
              otherImages[activeImage],
              space.title,
              "csp-gallery__main-img",
              activeImage,
              true
            )
          ) : (
            images[0] ? (
              renderImageWithFallback(
                images[0],
                space.title,
                "csp-gallery__main-img",
                0,
                true
              )
            ) : (
              <div className="csp-image-fallback">
                <span>Image not available</span>
              </div>
            )
          )}
          <div className="csp-gallery__overlay" />
          <button
            className="csp-gallery__badge"
            onClick={(e) => {
              e.stopPropagation();
              setPreviewIndex(0);
              setPreviewOpen(true);
            }}
            aria-label="View gallery"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            {otherImages.length > 0 ? otherImages.length : 0} photo{otherImages.length !== 1 ? "s" : ""}
          </button>
        </div>

        {otherImages.length > 0 && (
          <div className="csp-gallery__strip">
            {otherImages.map((img, i) => (
              <button
                key={i}
                className={`csp-gallery__thumb${activeImage === i ? " csp-gallery__thumb--on" : ""
                  }`}
                onClick={() => setActiveImage(i)}
                aria-label={`View photo ${i + 1}`}
              >
                {renderImageWithFallback(
                  img,
                  `Thumbnail ${i + 1}`,
                  "csp-gallery__thumb-img",
                  i
                )}
                {i === 4 && otherImages.length > 5 && (
                  <span className="csp-gallery__more">
                    +{otherImages.length - 5}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Body */}
      <div className="csp-body">
        {/* Left Content */}
        <main className="csp-content">
          {/* Breadcrumb */}
          <nav className="csp-breadcrumb" aria-label="Breadcrumb">
            <span>Commercial Spaces</span>
            <span className="csp-bc-sep">/</span>
            <span>{space.location?.city || "Listings"}</span>
            <span className="csp-bc-sep">/</span>
            <span className="csp-bc-active">{space.title}</span>
          </nav>

          {/* Header */}
          <div className="csp-header">
            <div className="csp-chips">
              <span className="csp-chip csp-chip--type">
                {space.propertyDetails?.propertyType || "Commercial"}
              </span>
              <span
                className={`csp-chip csp-chip--status-${space.status === "active" ? "active" : "inactive"
                  }`}
              >
                {space.status}
              </span>
              {isAdmin && (
                <span
                  className={`csp-chip csp-chip--approval-${space.approvalStatus}`}
                >
                  {space.approvalStatus}
                </span>
              )}
              {space.isFeatured && (
                <span className="csp-chip csp-chip--featured">⭐ Featured</span>
              )}
              {space.pricing?.negotiable && (
                <span className="csp-chip csp-chip--nego">Negotiable</span>
              )}
            </div>

            <h1 className="csp-title">{space.title}</h1>

            {fullLocation && (
              <p className="csp-location">
                <EnvironmentOutlined />
                {fullLocation}
              </p>
            )}
          </div>

          {/* Stats Bar */}
          <div className="csp-stats">
            <div className="csp-stat">
              <span className="csp-stat__icon">
                <ColumnWidthOutlined />
              </span>
              <span className="csp-stat__val">{space.propertyDetails?.areaSqFt || 0} sqft</span>
              <span className="csp-stat__lbl">Area</span>
            </div>
            <div className="csp-stat__div" />

            <div className="csp-stat">
              <span className="csp-stat__icon">
                <ApartmentOutlined />
              </span>
              <span className="csp-stat__val">{space.propertyDetails?.floor || "—"}</span>
              <span className="csp-stat__lbl">Floor</span>
            </div>
            <div className="csp-stat__div" />

            <div className="csp-stat">
              <span className="csp-stat__icon">
                <HomeOutlined />
              </span>
              <span className="csp-stat__val">{space.propertyDetails?.furnished ? "Yes" : "No"}</span>
              <span className="csp-stat__lbl">Furnished</span>
            </div>
            <div className="csp-stat__div" />

            <div className="csp-stat">
              <span className="csp-stat__icon">
                <BankOutlined />
              </span>
              <span className="csp-stat__val">{space.pricing?.type?.toUpperCase() || "—"}</span>
              <span className="csp-stat__lbl">Pricing Type</span>
            </div>

            {isAdmin && (
              <>
                <div className="csp-stat__div" />
                <div className="csp-stat">
                  <span className="csp-stat__icon">
                    <EyeOutlined />
                  </span>
                  <span className="csp-stat__val">{space.views || 0}</span>
                  <span className="csp-stat__lbl">Views</span>
                </div>
              </>
            )}
          </div>

          {/* Tabs */}
          <div className="csp-tabs">
            <div className="csp-tabs__nav" role="tablist">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  role="tab"
                  aria-selected={activeTab === tab}
                  className={`csp-tabs__btn${activeTab === tab ? " csp-tabs__btn--on" : ""
                    }`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="csp-tabs__panel">
              {activeTab === "Details" && (
                <div className="csp-detail-grid">
                  <div className="csp-detail-card">
                    <h3 className="csp-detail-card__head">Property Details</h3>
                    <table className="csp-detail-table">
                      <tbody>
                        {isAdmin && renderDetailRow("Listing ID", space._id)}
                        {renderDetailRow("Property Type", space.propertyDetails?.propertyType)}
                        {renderDetailRow("Building Type", space.propertyDetails?.buildingType)}
                        {renderDetailRow("Area", space.propertyDetails?.areaSqFt ? `${space.propertyDetails.areaSqFt} sqft` : null)}
                        {renderDetailRow("Floor", space.propertyDetails?.floor)}
                        {renderDetailRow("Total Floors", space.propertyDetails?.totalFloors)}
                        {renderDetailRow("Furnished", space.propertyDetails?.furnished ? "Yes" : "No")}
                        {renderDetailRow("Property Age", space.propertyDetails?.propertyAge ? `${space.propertyDetails.propertyAge} years` : null)}
                        {renderDetailRow("Country", space.location?.country)}
                        {renderDetailRow("Pincode", space.location?.pincode)}
                        {isAdmin && renderDetailRow("Slug", space.slug)}
                        {isAdmin && renderDetailRow("Latitude", space.location?.coordinates?.coordinates?.[1])}
                        {isAdmin && renderDetailRow("Longitude", space.location?.coordinates?.coordinates?.[0])}
                      </tbody>
                    </table>
                  </div>

                  <div className="csp-detail-card">
                    <h3 className="csp-detail-card__head">Pricing & Listing</h3>
                    <table className="csp-detail-table">
                      <tbody>
                        {renderDetailRow("Pricing Type", space.pricing?.type?.toUpperCase())}
                        {renderDetailRow("Price", space.pricing?.price ? `₹ ${space.pricing.price.toLocaleString("en-IN")}` : null)}
                        {renderDetailRow("Deposit", space.pricing?.deposit ? `₹ ${space.pricing.deposit.toLocaleString("en-IN")}` : null)}
                        {renderDetailRow("Negotiable", space.pricing?.negotiable ? "Yes" : "No")}
                        {renderDetailRow("Status", space.status)}
                        {isAdmin && renderDetailRow("Approval", space.approvalStatus)}
                        {isAdmin && renderDetailRow("Views", space.views || 0)}
                        {isAdmin && renderDetailRow("Expiry Date", space.expiryDate ? new Date(space.expiryDate).toLocaleDateString() : null)}
                        {isAdmin && renderDetailRow("Featured Until", space.featuredUntil ? new Date(space.featuredUntil).toLocaleDateString() : null)}
                        {renderDetailRow("Posted On", space.createdAt ? new Date(space.createdAt).toLocaleDateString() : null)}
                        {isAdmin && renderDetailRow("Updated On", space.updatedAt ? new Date(space.updatedAt).toLocaleDateString() : null)}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {activeTab === "Description" && (
                <div className="csp-desc-card">
                  <p className="csp-desc-text">
                    {space.description || "No description available."}
                  </p>
                </div>
              )}

              {activeTab === "Location" && (
                <div className="csp-location-wrap">
                  <div className="csp-detail-grid">
                    <div className="csp-detail-card">
                      <h3 className="csp-detail-card__head">Address</h3>
                      <table className="csp-detail-table">
                        <tbody>
                          {renderDetailRow("Address", space.location?.address)}
                          {renderDetailRow("Locality", space.location?.locality)}
                          {renderDetailRow("City", space.location?.city)}
                          {renderDetailRow("State", space.location?.state)}
                          {renderDetailRow("Country", space.location?.country)}
                          {renderDetailRow("Pincode", space.location?.pincode)}
                        </tbody>
                      </table>
                    </div>
                    <div className="csp-detail-card">
                      <h3 className="csp-detail-card__head">Contact Info</h3>
                      <table className="csp-detail-table">
                        <tbody>
                          {renderDetailRow("Owner", space.ownerId?.name)}
                          {isAdmin && renderDetailRow("Owner Email", space.ownerId?.email)}
                          {renderDetailRow("Contact Name", space.contactDetails?.contactName)}
                          {renderDetailRow("Phone", space.contactDetails?.phone)}
                          {renderDetailRow("Contact Email", space.contactDetails?.email)}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {space.location?.coordinates?.coordinates && (
                    <div className="csp-map">
                      <iframe
                        width="100%"
                        height="360"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        title="Location Map"
                        src={`https://www.google.com/maps?q=${space.location.coordinates.coordinates[1]},${space.location.coordinates.coordinates[0]}&z=15&output=embed`}
                      />
                    </div>
                  )}
                </div>
              )}

              {activeTab === "Amenities" && (
                <div className="csp-amenities-card">
                  <h3 className="csp-detail-card__head">Amenities</h3>
                  <div className="csp-amenities-list">
                    {space.amenities?.length > 0 ? (
                      <div className="csp-amenities-grid">
                        {space.amenities.map((item, index) => (
                          <div key={index} className="csp-amenity-item">
                            <span className="csp-amenity__dot" />
                            {item}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="csp-empty-msg">No amenities listed for this space.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Rejection Notice */}
          {space.approvalStatus === "rejected" && space.rejectionReason && (
            <div className="csp-rejection">
              <strong>Rejection Reason</strong>
              <p>{space.rejectionReason}</p>
            </div>
          )}
        </main>

        {/* Sidebar */}
        <aside className="csp-sidebar">
          <div className="csp-sidebar__card">
            <div className="csp-sidebar__price">
              <span className="csp-sidebar__amt">
                ₹ {space.pricing?.price?.toLocaleString("en-IN") || 0}
              </span>
              <span className="csp-sidebar__per">
                {getPriceLabel(space.pricing?.type)}
              </span>
            </div>

            <hr className="csp-sidebar__rule" />

            <div className="csp-sidebar__owner">
              <span className="csp-sidebar__avatar" aria-hidden="true">
                <UserOutlined />
              </span>
              <div className="csp-sidebar__owner-info">
                <span className="csp-sidebar__owner-name">
                  {space.contactDetails?.contactName || "Contact Owner"}
                </span>
                <span className="csp-sidebar__owner-role">Property Owner</span>
              </div>
            </div>

            {(space.contactDetails?.phone || space.contactDetails?.email) && (
              <dl className="csp-sidebar__contact-list">
                {space.contactDetails?.phone && (
                  <>
                    <dt>Phone</dt>
                    <dd>{space.contactDetails.phone}</dd>
                  </>
                )}
                {space.contactDetails?.email && (
                  <>
                    <dt>Email</dt>
                    <dd className="csp-sidebar__email">{space.contactDetails.email}</dd>
                  </>
                )}
              </dl>
            )}

            {phone && (
              <div className="csp-sidebar__actions">
                <a href={`tel:${phone}`} className="csp-btn csp-btn--call">
                  <PhoneOutlined style={{ transform: "rotate(90deg)" }} />
                  Call Now
                </a>
                <a
                  href={`https://wa.me/${phone}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="csp-btn csp-btn--wa"
                >
                  <FaWhatsapp />
                  WhatsApp
                </a>
              </div>
            )}

            <hr className="csp-sidebar__rule" />

            <dl className="csp-sidebar__facts">
              <div className="csp-sidebar__fact">
                <dt>Area</dt>
                <dd>{space.propertyDetails?.areaSqFt || 0} sqft</dd>
              </div>
              <div className="csp-sidebar__fact">
                <dt>Floor</dt>
                <dd>{space.propertyDetails?.floor || "—"}</dd>
              </div>
              <div className="csp-sidebar__fact">
                <dt>Furnished</dt>
                <dd>{space.propertyDetails?.furnished ? "Yes" : "No"}</dd>
              </div>
              <div className="csp-sidebar__fact">
                <dt>Pricing Type</dt>
                <dd>{space.pricing?.type?.toUpperCase() || "—"}</dd>
              </div>
              <div className="csp-sidebar__fact">
                <dt>Posted</dt>
                <dd>
                  {space.createdAt
                    ? new Date(space.createdAt).toLocaleDateString()
                    : "—"}
                </dd>
              </div>
            </dl>

            {isAdmin && (
              <div className="csp-sidebar__admin">
                <span className="csp-sidebar__admin-label">
                  Listed by (Admin)
                </span>
                <span>{space.ownerId?.name || "N/A"}</span>
                <span>{space.ownerId?.email || "N/A"}</span>
              </div>
            )}
          </div>

          <p className="csp-sidebar__note">
            Listed on{" "}
            {space.createdAt
              ? new Date(space.createdAt).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
              : "—"}
          </p>
        </aside>
      </div>

      {/* Mobile Sticky Footer */}
      {phone && (
        <div
          className="csp-mobile-bar"
          role="complementary"
          aria-label="Contact actions"
        >
          <div className="csp-mobile-bar__price">
            <span className="csp-mobile-bar__amt">
              ₹ {space.pricing?.price?.toLocaleString("en-IN") || 0}
            </span>
            <span className="csp-mobile-bar__per">
              {getPriceLabel(space.pricing?.type)}
            </span>
          </div>
          <div className="csp-mobile-bar__btns">
            <a href={`tel:${phone}`} className="csp-mbtn csp-mbtn--call">
              <PhoneOutlined style={{ transform: "rotate(90deg)" }} /> Call
            </a>
            <a
              href={`https://wa.me/${phone}`}
              target="_blank"
              rel="noopener noreferrer"
              className="csp-mbtn csp-mbtn--wa"
            >
              <FaWhatsapp /> WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {previewOpen && (
        <div
          className="csp-lb"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="csp-lb__blur"
            style={{ backgroundImage: `url(${otherImages[previewIndex] || images[0]})` }}
          />

          <button
            className="csp-lb__close"
            onClick={() => setPreviewOpen(false)}
            aria-label="Close preview"
          >
            ✕
          </button>

          <span className="csp-lb__counter">
            {previewIndex + 1} / {otherImages.length}
          </span>

          <div className="csp-lb__stage" onClick={(e) => e.stopPropagation()}>
            <button
              className="csp-lb__nav csp-lb__nav--prev"
              onClick={handlePrev}
              aria-label="Previous image"
            >
              ‹
            </button>
            {renderImageWithFallback(
              otherImages[previewIndex],
              `Preview ${previewIndex + 1}`,
              "csp-lb__img",
              previewIndex
            )}
            <button
              className="csp-lb__nav csp-lb__nav--next"
              onClick={handleNext}
              aria-label="Next image"
            >
              ›
            </button>
          </div>

          {otherImages.length > 1 && (
            <div
              className="csp-lb__strip"
              ref={stripRef}
              onClick={(e) => e.stopPropagation()}
            >
              {otherImages.map((img, i) => (
                <button
                  key={i}
                  className={`csp-lb__thumb${previewIndex === i ? " csp-lb__thumb--on" : ""
                    }`}
                  onClick={() => setPreviewIndex(i)}
                  aria-label={`Go to photo ${i + 1}`}
                >
                  {renderImageWithFallback(img, `Thumbnail ${i + 1}`, "", i)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CommercialSpaceDetails;